// src/components/JobView/SendComponentsToCatalogModal.js
// Envía los COMPONENTES de un trabajo (Servicio/Proyecto) al Catálogo de Productos
// como SKUs reutilizables (reorder-ready). El usuario elige la categoría en el momento.
// Dedupe por cliente + código (ver lib/products.upsertProductsFromComponents).
import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { X, PackagePlus, CheckCircle2, AlertTriangle } from "lucide-react";
import { upsertProductsFromComponents } from "../../lib/products";
import { resolveCategories } from "../../lib/productCategories";

const SendComponentsToCatalogModal = ({ isOpen, onClose, db, user, jobData }) => {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const components = (jobData?.components || []).filter((c) => (c?.name || "").trim() && (c?.code || "").trim());
  const ignored = (jobData?.components || []).length - components.length;

  useEffect(() => {
    if (!isOpen) return;
    setResult(null); setError("");
    (async () => {
      try {
        const cfg = await getDoc(doc(db, "project_settings", "productCategories"));
        const cats = resolveCategories(cfg.exists() ? cfg.data().categories : null);
        setCategories(cats);
        setCategoryId((prev) => prev || cats[0]?.id || "");
      } catch (e) { setError("No se pudieron cargar las categorías."); }
    })();
  }, [isOpen, db]);

  if (!isOpen) return null;

  const handleSend = async () => {
    setBusy(true); setError("");
    try {
      const r = await upsertProductsFromComponents(db, {
        components: jobData.components || [],
        clientId: jobData.clientId || null,
        categoryId,
        lastJobId: jobData.id || null,
        user,
      });
      setResult(r);
    } catch (e) { setError(e.message || "No se pudo enviar al catálogo."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black bg-opacity-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold flex items-center gap-2"><PackagePlus size={20} /> Enviar componentes al Catálogo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X /></button>
        </header>

        <div className="p-5 overflow-y-auto space-y-4">
          <p className="text-sm text-gray-600">
            Crea o actualiza un <b>SKU reutilizable</b> en el Catálogo de Productos por cada componente con
            <b> nombre y código</b>. Si ya existe un producto de este cliente con el mismo código, se actualiza
            (nombre/imagen/material) en vez de duplicar.
          </p>

          {error && <div className="p-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}

          {result ? (
            <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm flex items-start gap-2">
              <CheckCircle2 size={18} className="mt-0.5" />
              <div>
                <b>Listo.</b> Creados: {result.created} · Actualizados: {result.updated}
                {result.skipped > 0 && <> · Ignorados (sin nombre/código): {result.skipped}</>}.
                <div className="text-green-700 mt-1">Revisa el Catálogo de Productos.</div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Categoría para estos componentes</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm">
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr><th className="px-3 py-2 text-left">Componente</th><th className="px-3 py-2 text-left">Código</th><th className="px-3 py-2 text-center">Imagen</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {components.length === 0 && (
                      <tr><td colSpan="3" className="px-3 py-4 text-center text-gray-400">No hay componentes con nombre y código.</td></tr>
                    )}
                    {components.map((c, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{c.name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                        <td className="px-3 py-2 text-center">{c.imageUrl ? <span className="text-green-600">✔</span> : <span className="text-gray-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ignored > 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle size={14} /> {ignored} componente(s) sin nombre/código se ignorarán.</p>
              )}
            </>
          )}
        </div>

        <footer className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm">{result ? "Cerrar" : "Cancelar"}</button>
          {!result && (
            <button onClick={handleSend} disabled={busy || components.length === 0 || !categoryId} className="px-5 py-2 rounded-lg bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 disabled:opacity-50">
              {busy ? "Enviando…" : `Enviar ${components.length} al Catálogo`}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default SendComponentsToCatalogModal;
